import React, { useEffect, useState } from "react";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { useOfflineStore } from "presentation/stores/use-offline-store";

interface OfflineBoundaryProps {
  children: React.ReactNode;
}

export const OfflineBoundary: React.FC<OfflineBoundaryProps> = ({ children }) => {
  const { isOffline, setOffline } = useOfflineStore();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Sincroniza o estado inicial no carregamento da página
    if (!navigator.onLine) {
      setOffline(true);
    }

    const handleOnline = () => setOffline(false);
    const handleOffline = () => setOffline(true);

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, [setOffline]);

  const handleRetry = async () => {
    console.log(" [OfflineBoundary] handleRetry chamado");
    console.log(" [OfflineBoundary] navigator.onLine:", navigator.onLine);
    setLoading(true);

    const minDelay = new Promise((resolve) => setTimeout(resolve, 500)); // Pequeno delay para feedback visual

    // Verificação primária do navegador
    if (!navigator.onLine) {
      console.log(" [OfflineBoundary] Sem rede, retornando imediatamente");
      await minDelay; // Pequeno delay para feedback visual
      setOffline(true);
      setLoading(false);
      return;
    }

    console.log(" [OfflineBoundary] A tentar fetch HEAD...");

    try {
      // Ping leve utilizando a variável de ambiente da API (com fallback dinâmico para o origin da janela)
      const baseUrl = process.env.REACT_APP_BASE_URL || window.location.origin;
      console.log(" [OfflineBoundary] baseUrl:", baseUrl);

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 4000); // Timeout de 4s para evitar espera excessiva em redes lentas

      await Promise.all([
        fetch(baseUrl, {
          method: "GET", mode: "no-cors"/*Evita problemas de CORS / 405 ao testar conectividade*/, cache: "no-store", signal: controller.signal,
        }),
        minDelay
      ])

      clearTimeout(timeoutId);
      console.log("[OfflineBoundary] Conexão ativa confirmada");
      setOffline(false);
    } catch (err) {
      console.log("[OfflineBoundary] Falha de conectividade:", err);
      setOffline(true);
    } finally {
      setLoading(false);
    }
  };

  if (isOffline) {

    return (
      <div className="flex align-items-center justify-content-center min-h-screen surface-ground p-4">
        <Card className="w-full max-w-28rem text-center shadow-4 border-round-xl">
          <div className="flex flex-column align-items-center">
            <div className="border-circle bg-red-50 p-4 mb-4 inline-flex align-items-center justify-content-center">
              <i className="pi pi-wifi-off text-6xl text-red-500" />
            </div>

            <h2 className="text-2xl font-bold text-900 mb-2">Sem Conexão à Internet</h2>

            <p className="text-600 line-height-3 mb-5">
              Ocorreu uma falha na comunicação com o servidor ou o dispositivo está sem ligação à rede.
            </p>

            <Button
              label={loading ? "A verificar..." : "Tentar Novamente"}
              icon={loading ? "pi pi-spin pi-spinner" : "pi pi-refresh"}
              disabled={loading}
              className="p-button-primary w-full border-round-lg"
              onClick={handleRetry}
            />
          </div>
        </Card>
      </div>
    );
  }

  return <>{children}</>;
};

export default OfflineBoundary;